import type { Session as AnalyticsSession } from "@threekit/analytics";
import { OptionsType } from "@threekit/rest-api";

export type OptionsShowParams = Parameters<AnalyticsSession["optionsShow"]>[0];

export function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

export function isItemLikeAttributeType(type: string): boolean {
  const t = type.toLowerCase();
  return (
    type === "Asset" ||
    type === "PartReference" ||
    t === "partreference" ||
    t.includes("partreference")
  );
}

export function isAssetValue(v: unknown): v is {
  assetId: string;
  name?: string;
  label?: string;
  metadata?: unknown;
} {
  return (
    isRecord(v) &&
    typeof v.assetId === "string" &&
    v.assetId.length > 0
  );
}

export function valueToValueOption(v: unknown): {
  optionId: string;
  optionName: string;
} {
  if (isAssetValue(v)) {
    return {
      optionId: v.assetId,
      optionName: String(v.name ?? v.label ?? v.assetId),
    };
  }
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
    const s = String(v);
    return { optionId: s, optionName: s };
  }
  if (v === null || v === undefined) {
    return { optionId: "null", optionName: "null" };
  }
  try {
    const s = JSON.stringify(v);
    return { optionId: s, optionName: s };
  } catch {
    return { optionId: String(v), optionName: String(v) };
  }
}

export function buildOptionsShowForAttribute(
  attr: Record<string, unknown>,
  parentAssetId: string | undefined
): OptionsShowParams | null {
  const name = typeof attr.name === "string" ? attr.name : null;
  if (!name) return null;

  const rawValues = attr.values;
  if (!Array.isArray(rawValues) || rawValues.length === 0) return null;

  const typeStr = typeof attr.type === "string" ? attr.type : "";

  if (isItemLikeAttributeType(typeStr)) {
    const options = rawValues.filter(isAssetValue).map((v) => ({
      optionId: v.assetId,
      optionName: String(v.name ?? v.label ?? v.assetId),
    }));
    if (options.length === 0) return null;
    return {
      ...(parentAssetId ? { assetId: parentAssetId } : {}),
      optionsSetId: name,
      options,
      optionsType: OptionsType.Asset,
    } as OptionsShowParams;
  }

  const options = rawValues.map(valueToValueOption);
  return {
    ...(parentAssetId ? { assetId: parentAssetId } : {}),
    optionsSetId: name,
    options,
    optionsType: OptionsType.Value,
  } as OptionsShowParams;
}

export function getConfiguratorAttributeList(
  configurator: unknown,
  useDisplayAttributes: boolean
): unknown[] {
  const cfg = configurator as {
    getAttributes?: () => unknown;
    getDisplayAttributes?: (opts?: {
      includeHidden?: boolean;
      enabledOnly?: boolean;
    }) => unknown;
  };

  if (useDisplayAttributes && typeof cfg.getDisplayAttributes === "function") {
    const list = cfg.getDisplayAttributes({ includeHidden: true });
    return Array.isArray(list) ? list : [];
  }
  if (typeof cfg.getAttributes === "function") {
    const list = cfg.getAttributes();
    return Array.isArray(list) ? list : [];
  }
  throw new Error(
    "ThreekitEmbed: configurator has no getAttributes() or getDisplayAttributes()"
  );
}

/** One Threekit display attribute that maps to an Oracle CPQ field via `metadata.cpq_attribute`. */
export interface CpqLinkedDisplayAttribute {
  /**
   * Oracle CPQ / BigMachines attribute name — pass to `CPQJS.getAttributeVal(cpqAttribute)` (or your
   * wrapper) to read the current configuration value.
   */
  cpqAttribute: string;
  /**
   * Alternate Oracle CPQ / BigMachines attribute name from `metadata.cpq_attributeAlt`. Some
   * Threekit attributes map to a different CPQ field name depending on which model is selected
   * (the CPQ field that actually exists on the BOM changes with the model). When present, treat
   * `cpqAttributeAlt` exactly like `cpqAttribute` — read it, listen for its changes, and apply its
   * value — since only one of the two will actually exist for any given model.
   */
  cpqAttributeAlt?: string;
  /** Threekit attribute `name` (used in `setConfiguration` / `applyCPQSelections`). */
  threekitAttributeName: string;
  /** Threekit attribute `type` when present. */
  threekitType?: string;
  /**
   * `metadata.LeftRightMulti` ("Left"/"Right") when present — see `cpq-apply-selections.ts`.
   * Two Threekit attributes (e.g. "...Left" / "...Right") can share one `cpqAttribute`; only one
   * survives the "first occurrence wins" dedup below, so this reflects whichever of them (if
   * either) declared it.
   */
  leftRightMultiSide?: LeftRightSide;
  /**
   * Distinct Oracle CPQ qty attribute names from `metadata.leftRightQtyAttr` on this attribute's
   * values (and, for a `LeftRightMulti` pair, its sibling's values too — unioned even though only
   * one side survives the dedup below). These qty fields aren't linked to any Threekit attribute
   * via `cpq_attribute` themselves, so `registerCpqLinkedAttributeListeners` listens on them
   * separately and, on change, re-applies this row's current `cpqAttribute`/`cpqAttributeAlt`
   * value (see `cpq-linked-attribute-listeners.ts`).
   */
  leftRightQtyAttrs?: string[];
}

export type LeftRightSide = "Left" | "Right";

/** Normalizes an arbitrary metadata value to `"Left"`/`"Right"`, case-insensitively. */
export function normalizeLeftRightSide(value: unknown): LeftRightSide | null {
  if (typeof value !== "string") return null;
  const v = value.trim().toLowerCase();
  if (v === "left") return "Left";
  if (v === "right") return "Right";
  return null;
}

/**
 * Reads a display/configurator attribute's `metadata.LeftRightMulti` ("Left"/"Right") — see
 * {@link CpqLinkedDisplayAttribute.leftRightMultiSide}.
 */
export function getLeftRightMultiSide(attr: Record<string, unknown>): LeftRightSide | null {
  const meta = isRecord(attr.metadata) ? attr.metadata : undefined;
  if (!meta) return null;
  return normalizeLeftRightSide(meta.LeftRightMulti);
}

/**
 * Distinct Oracle CPQ qty attribute names from `metadata.leftRightQtyAttr` across an attribute's
 * `values` — see {@link CpqLinkedDisplayAttribute.leftRightQtyAttrs}.
 */
export function getLeftRightQtyAttrsFromValues(attr: Record<string, unknown>): string[] {
  const rawValues = attr.values;
  if (!Array.isArray(rawValues)) return [];
  const out = new Set<string>();
  for (const v of rawValues) {
    if (!isRecord(v)) continue;
    const meta = isRecord(v.metadata) ? v.metadata : undefined;
    const qtyAttr = meta?.leftRightQtyAttr;
    if (typeof qtyAttr !== "string" && typeof qtyAttr !== "number") continue;
    const s = String(qtyAttr).trim();
    if (s.length > 0) out.add(s);
  }
  return [...out];
}

/**
 * Reads `metadata.cpq_attribute` from a display/configurator attribute object (trimmed).
 * Matches the key used by `applyCPQSelections` when mapping Oracle CPQ fields to Threekit.
 */
export function getCpqAttributeKeyFromDisplayAttribute(
  attr: Record<string, unknown>
): string | undefined {
  const meta = isRecord(attr.metadata) ? attr.metadata : undefined;
  if (!meta) return undefined;
  const v = meta.cpq_attribute;
  if (typeof v === "string" || typeof v === "number") {
    const s = String(v).trim();
    return s.length > 0 ? s : undefined;
  }
  return undefined;
}

/**
 * Reads `metadata.cpq_attributeAlt` from a display/configurator attribute object (trimmed) — the
 * alternate Oracle CPQ field name used when a different model is selected. See
 * {@link CpqLinkedDisplayAttribute.cpqAttributeAlt}.
 */
export function getCpqAttributeAltKeyFromDisplayAttribute(
  attr: Record<string, unknown>
): string | undefined {
  const meta = isRecord(attr.metadata) ? attr.metadata : undefined;
  if (!meta) return undefined;
  const v = meta.cpq_attributeAlt;
  if (typeof v === "string" || typeof v === "number") {
    const s = String(v).trim();
    return s.length > 0 ? s : undefined;
  }
  return undefined;
}

/**
 * From the array returned by `configurator.getDisplayAttributes({ includeHidden: true })` (or the
 * result of {@link getConfiguratorAttributeList} with `useDisplayAttributes: true`), returns every
 * attribute that defines `metadata.cpq_attribute`, for syncing BOM values from Oracle CPQ into
 * Threekit via `applyCPQSelections` in `embed-api.ts`. Each row also carries `cpqAttributeAlt` when
 * the attribute defines `metadata.cpq_attributeAlt` (see {@link CpqLinkedDisplayAttribute}).
 *
 * If multiple Threekit attributes share the same `cpq_attribute` (or `cpq_attributeAlt`), only the
 * first occurrence is kept.
 */
export function listCpqLinkedDisplayAttributes(
  displayAttributes: unknown[]
): CpqLinkedDisplayAttribute[] {
  const seen = new Set<string>();
  const out: CpqLinkedDisplayAttribute[] = [];

  for (const raw of displayAttributes) {
    if (!isRecord(raw)) continue;
    const threekitAttributeName = typeof raw.name === "string" ? raw.name : "";
    if (!threekitAttributeName) continue;

    const cpqAttribute = getCpqAttributeKeyFromDisplayAttribute(raw);
    if (!cpqAttribute) continue;
    if (seen.has(cpqAttribute)) continue;
    seen.add(cpqAttribute);

    const cpqAttributeAlt = getCpqAttributeAltKeyFromDisplayAttribute(raw);
    if (cpqAttributeAlt) seen.add(cpqAttributeAlt);

    const threekitType = typeof raw.type === "string" ? raw.type : undefined;
    out.push({
      cpqAttribute,
      ...(cpqAttributeAlt ? { cpqAttributeAlt } : {}),
      threekitAttributeName,
      threekitType,
    });
  }

  // Enrich with LeftRightMulti info, unioned across EVERY attribute declaring a given
  // `cpq_attribute`/`cpq_attributeAlt` key — not just the single representative row kept above.
  // A LeftRightMulti pair's Left and Right attributes both declare the same `cpq_attribute`, but
  // the "first occurrence wins" dedup above keeps only one of them, so this second pass walks
  // `displayAttributes` again to recover whatever the dropped sibling(s) declared.
  const qtyAttrsByKey = new Map<string, Set<string>>();
  const sideByKey = new Map<string, LeftRightSide>();
  for (const raw of displayAttributes) {
    if (!isRecord(raw)) continue;
    const keys = [
      getCpqAttributeKeyFromDisplayAttribute(raw),
      getCpqAttributeAltKeyFromDisplayAttribute(raw),
    ].filter((k): k is string => !!k);
    if (keys.length === 0) continue;

    const side = getLeftRightMultiSide(raw);
    const qtyAttrs = getLeftRightQtyAttrsFromValues(raw);
    if (!side && qtyAttrs.length === 0) continue;

    for (const key of keys) {
      if (side) sideByKey.set(key, side);
      if (qtyAttrs.length > 0) {
        const set = qtyAttrsByKey.get(key) ?? new Set<string>();
        for (const q of qtyAttrs) set.add(q);
        qtyAttrsByKey.set(key, set);
      }
    }
  }

  for (const row of out) {
    const side =
      sideByKey.get(row.cpqAttribute) ??
      (row.cpqAttributeAlt ? sideByKey.get(row.cpqAttributeAlt) : undefined);
    if (side) row.leftRightMultiSide = side;

    const qtySet = new Set<string>();
    for (const key of [row.cpqAttribute, row.cpqAttributeAlt]) {
      if (!key) continue;
      const set = qtyAttrsByKey.get(key);
      if (set) for (const q of set) qtySet.add(q);
    }
    if (qtySet.size > 0) row.leftRightQtyAttrs = [...qtySet];
  }

  return out;
}

/**
 * Same as {@link listCpqLinkedDisplayAttributes} after reading attributes from the configurator
 * with `getDisplayAttributes({ includeHidden: true })`.
 */
export function listCpqLinkedDisplayAttributesFromConfigurator(
  configurator: unknown
): CpqLinkedDisplayAttribute[] {
  const list = getConfiguratorAttributeList(configurator, true);
  return listCpqLinkedDisplayAttributes(list);
}
